import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Avatar,
    Breadcrumbs,
    Link,
    Button,
    Divider,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ArrowBack as ArrowBackIcon,
    Person as PersonIcon,
    CheckCircle as CheckCircleIcon,
    RadioButtonUnchecked as RadioButtonUncheckedIcon
} from '@mui/icons-material';
import { useParams } from 'react-router';
import { useOktaAuth } from '@okta/okta-react';
import PatientTestResults from './PatientTestResults';
import AxiosInstance from '../configs/axiosConfig';
import OnboardQuestionnaireJSON from '../utils/OnboardQuestionnaire.json';
import HowWellCanYouSeeJSON from '../utils/HowWellCanYouSee.json';
import ExitQuestionnaireJSON from '../utils/ExitQuestionnaire.json';
import {
    OnboardingQuestionnaireResponse,
    DailyQuestionnaireResponse,
    ExitQuestionnaireResponse,
    QuestionnaireQuestion,
    Question
} from '../types/questionnaire.types';

const PatientAnswers1: React.FC = () => {
    const { patientId } = useParams<{ patientId: string }>();
    const { userid } = useParams<{ userid: string }>();
    const { authState } = useOktaAuth();
    const [expandedPanel, setExpandedPanel] = useState<string | false>(false);
    const [questionnaireData, setQuestionnaireData] = useState<OnboardingQuestionnaireResponse | null>(null);
    const [dailyQuestionnaires, setDailyQuestionnaires] = useState<DailyQuestionnaireResponse[]>([]);
    const [exitQuestionnaire, setExitQuestionnaire] = useState<ExitQuestionnaireResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [questionsByCategory, setQuestionsByCategory] = useState<Record<string, Question[]>>({});

    // Get userid from URL params (you may need to adjust this based on your routing)
    const urlParams = new URLSearchParams(window.location.search);
    // const userid = urlParams.get('userid') || "00uqDpoi2u4WVdaAr1d7"; // fallback for demo

    const patientData = {
        id: patientId,
        name: "John Doe",
        studyName: "MF Lens Study",
        completedDate: "08/09/2025 13:46"
    };

    // Helper function to parse array values from API response
    const parseArrayValue = (value: string): string[] => {
        try {
            // Remove brackets and split by comma
            const cleanValue = value.replace(/^\[|\]$/g, '').trim();
            if (!cleanValue) return [];
            return cleanValue.split(',').map(item => item.trim());
        } catch {
            return [];
        }
    };

    // Helper function to get answer text from API response
    const getAnswerText = (
        questionKey: string, 
        apiValue: string, 
        questionData: QuestionnaireQuestion
    ): string => {
        if (!apiValue) return 'Not answered';

        // Handle multi-value questions (arrays)
        if (questionData.isMultiValue) {
            const selectedKeys = parseArrayValue(apiValue);
            const selectedTitles = selectedKeys.map(key => {
                const option = questionData.options.find(opt => 
                    opt.title.toLowerCase() === key.toLowerCase() || 
                    opt.key.toLowerCase() === key.toLowerCase()
                );
                return option ? option.title : key;
            });
            return selectedTitles.join(', ') || apiValue;
        }

        // Handle numeric rating questions
        if (questionData.format === 'numbers' && questionData.dispalyValueForNumbers) {
            const description = questionData.dispalyValueForNumbers[apiValue];
            return description ? `${apiValue} - ${description}` : apiValue;
        }

        // Handle single select options
        const matchedOption = questionData.options.find(opt => 
            opt.title.toLowerCase() === apiValue.toLowerCase() || 
            opt.key.toLowerCase() === apiValue.toLowerCase()
        );

        return matchedOption ? matchedOption.title : apiValue;
    };

    // Helper function to get dependent answer text
    const getDependentAnswerText = (
        questionData: QuestionnaireQuestion,
        parentValue: string,
        dependentValue: string
    ): string => {
        if (!dependentValue) return '';

        const parentOption = questionData.options.find(opt => 
            opt.title.toLowerCase() === parentValue.toLowerCase() || 
            opt.key.toLowerCase() === parentValue.toLowerCase()
        );

        if (!parentOption || !parentOption.dependentOptions) return dependentValue;

        const matchedDependentOption = parentOption.dependentOptions.find(opt => 
            opt.title.toLowerCase() === dependentValue.toLowerCase() || 
            opt.key.toLowerCase() === dependentValue.toLowerCase()
        );

        return matchedDependentOption ? matchedDependentOption.title : dependentValue;
    };

    // Fetch onboarding questionnaire data
    useEffect(() => {
        const fetchQuestionnaireData = async () => {
            if (!userid) {
                setError('User ID is required');
                setLoading(false);
                return;
            }

            // Don't fetch data if not authenticated
            if (!authState?.isAuthenticated) {
                console.log('⏸️ Waiting for authentication before fetching questionnaire data');
                setLoading(true);
                return;
            }

            console.log('🚀 Auth state ready, fetching questionnaire data');

            try {
                setLoading(true);
                
                // Fetch onboarding, daily, and exit questionnaires in parallel
                const results = await Promise.allSettled([
                    AxiosInstance.get<OnboardingQuestionnaireResponse>(
                        `/patients/questionnaire/onboarding?call=webapp&userId=${userid}`
                    ),
                    AxiosInstance.get<DailyQuestionnaireResponse[]>(
                        `/patients/questionnaire/daily?call=webapp&userId=${userid}`
                    ),
                    AxiosInstance.get<ExitQuestionnaireResponse>(
                        `/patients/questionnaire/exit?call=webapp&userId=${userid}`
                    )
                ]);

                // Process results individually - handle partial success/failure
                const [onboardingResult, dailyResult, exitResult] = results;
                
                // Handle onboarding questionnaire
                if (onboardingResult.status === 'fulfilled') {
                    setQuestionnaireData(onboardingResult.value.data);
                } else {
                    console.error('Failed to fetch onboarding questionnaire:', onboardingResult.reason);
                    setQuestionnaireData(null);
                }

                // Handle daily questionnaires
                if (dailyResult.status === 'fulfilled') {
                    setDailyQuestionnaires(dailyResult.value.data || []);
                } else {
                    console.error('Failed to fetch daily questionnaires:', dailyResult.reason);
                    setDailyQuestionnaires([]);
                }

                // Handle exit questionnaire
                if (exitResult.status === 'fulfilled') {
                    setExitQuestionnaire(exitResult.value.data);
                } else {
                    console.error('Failed to fetch exit questionnaire:', exitResult.reason);
                    setExitQuestionnaire(null);
                }

                // Map responses to questions with available data
                mapResponseToQuestions(
                    onboardingResult.status === 'fulfilled' ? onboardingResult.value.data : null, 
                    dailyResult.status === 'fulfilled' ? (dailyResult.value.data || []) : [], 
                    exitResult.status === 'fulfilled' ? exitResult.value.data : null
                );

                // Set error message based on failed requests
                const failedRequests = results.filter(result => result.status === 'rejected');
                if (failedRequests.length === results.length) {
                    // All requests failed
                    setError('Failed to load all questionnaire data');
                } else if (failedRequests.length > 0) {
                    // Some requests failed
                    const errorMessages = failedRequests.map((result, index) => {
                        const requestNames = ['onboarding', 'daily', 'exit'];
                        return `${requestNames[results.indexOf(result)]} questionnaire`;
                    });
                    setError(`Partially loaded data. Failed to load: ${errorMessages.join(', ')}`);
                } else {
                    // All requests succeeded
                    setError(null);
                }
            } catch (err: any) {
                console.error('Unexpected error fetching questionnaire data:', err);
                setError('An unexpected error occurred while loading questionnaire data');
            } finally {
                setLoading(false);
            }
        };

        fetchQuestionnaireData();
    }, [userid, authState?.isAuthenticated]);

    // Map API response to questionnaire questions
    const mapResponseToQuestions = (
        onboardingData: OnboardingQuestionnaireResponse | null,
        dailyData: DailyQuestionnaireResponse[],
        exitData: ExitQuestionnaireResponse | null
    ) => {
        const categories: Record<string, Question[]> = {};

        // Map Onboarding Questionnaire
        if (onboardingData) {
            const onboardingQuestions = OnboardQuestionnaireJSON as QuestionnaireQuestion[];
            categories["Onboarding Questionnaire"] = [];

            onboardingQuestions.forEach((q) => {
            if (!q.key || q.format === 'heading') return;

            const apiValue = onboardingData[q.key as keyof OnboardingQuestionnaireResponse];
            if (apiValue === null || apiValue === undefined) return;

            const answerText = getAnswerText(q.key, apiValue as string, q);

            const question: Question = {
                id: `q${q.qid}`,
                question: q.question,
                answer: answerText,
                category: "Onboarding Questionnaire",
                answeredAt: onboardingData.questionnaireCapturedOn || 'N/A',
                isRequired: true
            };

            categories["Onboarding Questionnaire"].push(question);

            // Handle dependent questions
            if (q.hasDependencies && q.dependentKey && q.dependentQuestion) {
                const dependentValue = onboardingData[q.dependentKey as keyof OnboardingQuestionnaireResponse];
                if (dependentValue) {
                    const dependentAnswerText = getDependentAnswerText(q, apiValue as string, dependentValue as string);
                    
                    const dependentQuestion: Question = {
                        id: `q${q.qid}_dep`,
                        question: q.dependentQuestion,
                        answer: dependentAnswerText,
                        category: "Onboarding Questionnaire",
                        answeredAt: onboardingData.questionnaireCapturedOn || 'N/A',
                        isRequired: true
                    };

                    categories["Onboarding Questionnaire"].push(dependentQuestion);
                }
            }
        });
        }

        // Map Daily Questionnaires
        const dailyQuestions = HowWellCanYouSeeJSON as QuestionnaireQuestion[];
        
        dailyData.forEach((dailyEntry, index) => {
            const categoryName = `Daily Questionnaire - ${dailyEntry.uploadDate} (${new Date(parseInt(dailyEntry.uploadTimeStamp) * 1000).toLocaleTimeString()})`;
            categories[categoryName] = [];

            dailyQuestions.forEach((q) => {
                if (!q.key || q.format === 'heading') return;

                const apiValue = dailyEntry[q.key as keyof DailyQuestionnaireResponse];
                if (apiValue === null || apiValue === undefined) return;

                const answerText = getAnswerText(q.key, apiValue as string, q);

                const question: Question = {
                    id: `daily_${index}_q${q.qid}`,
                    question: q.question,
                    answer: answerText,
                    category: categoryName,
                    answeredAt: dailyEntry.uploadDate || 'N/A',
                    isRequired: true
                };

                categories[categoryName].push(question);

                // Handle dependent questions
                if (q.hasDependencies && q.dependentKey && q.dependentQuestion) {
                    const dependentValue = dailyEntry[q.dependentKey as keyof DailyQuestionnaireResponse];
                    if (dependentValue) {
                        const dependentAnswerText = getDependentAnswerText(q, apiValue as string, dependentValue as string);
                        
                        const dependentQuestion: Question = {
                            id: `daily_${index}_q${q.qid}_dep`,
                            question: q.dependentQuestion,
                            answer: dependentAnswerText,
                            category: categoryName,
                            answeredAt: dailyEntry.uploadDate || 'N/A',
                            isRequired: true
                        };

                        categories[categoryName].push(dependentQuestion);
                    }
                }
            });
        });

        // Map Exit Questionnaire
        if (exitData) {
            const exitQuestions = ExitQuestionnaireJSON as QuestionnaireQuestion[];
            categories["Exit Questionnaire"] = [];

            exitQuestions.forEach((q) => {
                if (!q.key || q.format === 'heading') return;

                const apiValue = exitData[q.key as keyof ExitQuestionnaireResponse];
                if (apiValue === null || apiValue === undefined) return;

                const answerText = getAnswerText(q.key, apiValue as string, q);

                const question: Question = {
                    id: `exit_q${q.qid}`,
                    question: q.question,
                    answer: answerText,
                    category: "Exit Questionnaire",
                    answeredAt: 'N/A',
                    isRequired: true
                };

                categories["Exit Questionnaire"].push(question);

                // Handle dependent questions
                if (q.hasDependencies && q.dependentKey && q.dependentQuestion) {
                    const dependentValue = exitData[q.dependentKey as keyof ExitQuestionnaireResponse];
                    if (dependentValue) {
                        const dependentAnswerText = getDependentAnswerText(q, apiValue as string, dependentValue as string);
                        
                        const dependentQuestion: Question = {
                            id: `exit_q${q.qid}_dep`,
                            question: q.dependentQuestion,
                            answer: dependentAnswerText,
                            category: "Exit Questionnaire",
                            answeredAt: 'N/A',
                            isRequired: true
                        };

                        categories["Exit Questionnaire"].push(dependentQuestion);
                    }
                }
            });
        }

        // Remove empty categories
        Object.keys(categories).forEach(key => {
            if (categories[key].length === 0) {
                delete categories[key];
            }
        });

        setQuestionsByCategory(categories);
    };

    const handlePanelChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedPanel(isExpanded ? panel : false);
    };

    return (
        <Box sx={{ p: 3, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Breadcrumbs sx={{ mb: 2 }}>
                    <Link color="inherit" href="/" sx={{ display: 'flex', alignItems: 'center' }}>
                        Dashboard
                    </Link>
                    <Link color="inherit" href="/patients">
                        Patients
                    </Link>
                    <Typography color="text.primary">Patient Results</Typography>
                </Breadcrumbs>
            </Box>

            {/* Patient Info Card */}
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#1976d2', width: 56, height: 56 }}>
                            <PersonIcon />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                Patient ID: {patientId}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                User ID: {userid}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                <Chip label={patientData.studyName} size="small" color="primary" />
                                {questionnaireData && (
                                    <Chip 
                                        label={`Onboarding: ${questionnaireData.questionnaireCapturedOn}`} 
                                        size="small" 
                                        variant="outlined" 
                                    />
                                )}
                                {dailyQuestionnaires.length > 0 && (
                                    <Chip 
                                        label={`Daily Entries: ${dailyQuestionnaires.length}`} 
                                        size="small" 
                                        color="secondary"
                                        variant="outlined" 
                                    />
                                )}
                                {exitQuestionnaire && (
                                    <Chip 
                                        label="Exit Completed" 
                                        size="small" 
                                        color="success"
                                        variant="outlined" 
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Test Results Section - Now appears before questionnaire */}
            <PatientTestResults userid={userid} />

            {/* Questionnaire Results Section */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 500, color: '#2c3e50', mb: 3 }}>
                    Questionnaire Results
                </Typography>

                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Loading questionnaire data...</Typography>
                    </Box>
                )}

                {/* Error State 
                {error && !loading && (
                    <Alert severity={error.includes('Partially loaded') ? 'warning' : 'error'} sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}*/}

                {/* Questions by Category */}
                {!loading && Object.keys(questionsByCategory).length === 0 && (
                    <Alert severity="info">
                        No questionnaire data available for this patient.
                    </Alert>
                )}

                {!loading && Object.entries(questionsByCategory).map(([category, questions]) => (
                    <Accordion
                        key={category}
                        expanded={expandedPanel === category}
                        onChange={handlePanelChange(category)}
                        sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, backgroundColor: '#ffffff' }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                backgroundColor: '#ffffff',
                                borderRadius: '8px 8px 0 0',
                                '&.Mui-expanded': { borderRadius: '8px 8px 0 0' }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                    {category}
                                </Typography>
                                <Chip
                                    label={`${questions.length} question${questions.length !== 1 ? 's' : ''}`}
                                    size="small"
                                    variant="outlined"
                                />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                            <TableContainer component={Paper} elevation={0}>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                            <TableCell sx={{ fontWeight: 600, width: '60px' }}>Status</TableCell>
                                            <TableCell sx={{ fontWeight: 600, width: '40%' }}>Question</TableCell>
                                            <TableCell sx={{ fontWeight: 600, width: '40%' }}>Answer</TableCell>
                                            <TableCell sx={{ fontWeight: 600, width: '20%' }}>Answered On</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {questions.map((question) => (
                                            <TableRow 
                                                key={question.id}
                                                sx={{ 
                                                    '&:hover': { backgroundColor: '#f9f9f9' },
                                                    '&:last-child td, &:last-child th': { border: 0 }
                                                }}
                                            >
                                                <TableCell align="center">
                                                    {question.answer && question.answer !== 'Not answered' ? (
                                                        <CheckCircleIcon color="success" />
                                                    ) : (
                                                        <RadioButtonUncheckedIcon color="disabled" />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {question.question}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ color: '#2c3e50' }}>
                                                        {question.answer}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {question.answeredAt}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        </Box>
    );
};

export default PatientAnswers1;